import { useEffect, useState } from "react";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { getParametrosReservacion, guardarParametrosReservacion } from "../services/portalService";
import { labelSt, fldStyle } from "./DatosPagoFields";

/**
 * ParametrosReservacionModal
 * ─────────────────────────────────────────────────────────────────────────────
 * Las reglas de reservación del condominio: hasta cuándo puede reservar un
 * residente, cuánto aguanta una solicitud sin revisar antes de rechazarse sola,
 * cuánto sobrevive una que ya pasó de fecha y si se le deja reservar a quien
 * está en mora.
 *
 * Todo arranca apagado salvo el tope de 30 días: encender una regla le cambia
 * al residente lo que puede hacer, y eso lo decide cada administración.
 */

const seccionSt = {
  fontSize: 12, fontWeight: 800, color: "#f97316", textTransform: "uppercase",
  letterSpacing: 0.6, display: "flex", alignItems: "center", gap: 7, margin: 0,
};

const ayudaSt = { margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 };

/**
 * La fecha más reciente que hoy ya se cancelaría con ese plazo.
 *
 * Mismo conteo que el backend (`fecha_limite_cancelacion`): el plazo arranca el
 * día siguiente a la reserva, así que se cancela cuando hoy pasó la fecha
 * reservada más el plazo. Con 3 días, la reserva del 20 se cancela el 24.
 */
function fechaCorteCancelacion(dias) {
  const n = Number(dias) || 0;
  if (n <= 0) return "";
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (n + 1));
  return d.toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
}

function Numero({ label, ayuda, value, onChange, min = 0, max = 730, sufijo = "días" }) {
  return (
    <div>
      <label style={labelSt}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="number" min={min} max={max} value={value}
          onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          style={fldStyle({ maxWidth: 120 })}
        />
        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{sufijo}</span>
      </div>
      {ayuda && <p style={ayudaSt}>{ayuda}</p>}
    </div>
  );
}

function Switch({ checked, onChange, titulo, ayuda }) {
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ marginTop: 3, accentColor: "#f97316" }} />
      <span>
        <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{titulo}</span>
        <span style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>{ayuda}</span>
      </span>
    </label>
  );
}

export default function ParametrosReservacionModal({ onClose, onSaved }) {
  const [p, setP]           = useState(null);
  const [cargando, setCarg] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    getParametrosReservacion()
      .then(setP)
      .catch(e => setError(e.message || "No se pudieron cargar los parámetros."))
      .finally(() => setCarg(false));
  }, []);

  function set(campo, valor) { setP(prev => ({ ...prev, [campo]: valor })); setError(""); }

  async function guardar() {
    setSaving(true); setError("");
    try {
      const guardado = await guardarParametrosReservacion({
        dias_max_anticipacion:   Number(p.dias_max_anticipacion)   || 0,
        dias_vence_pendiente:    Number(p.dias_vence_pendiente)    || 0,
        dias_aviso_previo:       Number(p.dias_aviso_previo)       || 0,
        dias_cancela_tras_fecha: Number(p.dias_cancela_tras_fecha) || 0,
        bloquear_morosos:        !!p.bloquear_morosos,
        max_facturas_vencidas:   Number(p.max_facturas_vencidas)   || 0,
        dias_gracia_vencimiento: Number(p.dias_gracia_vencimiento) || 0,
        monto_maximo_deuda:      String(p.monto_maximo_deuda ?? "0"),
        respetar_estado_cobro:   !!p.respetar_estado_cobro,
      });
      onSaved?.(guardado);
      onClose();
    } catch (e) {
      try { setError(Object.values(JSON.parse(e.message)).flat().join(". ")); }
      catch { setError(e.message || "No se pudieron guardar los parámetros."); }
    } finally { setSaving(false); }
  }

  return (
    <div {...overlayClose(onClose)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(4px)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: "min(640px, 96vw)",
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        maxHeight: "92vh", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>
              Parámetros de reservación
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
              Aplican a las reservas que piden los residentes desde el portal
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 22 }}>
          {cargando ? (
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)" }}>
              <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 7 }} /> Cargando…
            </p>
          ) : !p ? (
            <p style={{ margin: 0, fontSize: 13, color: "#f87171" }}>{error || "No se pudieron cargar los parámetros."}</p>
          ) : (
            <>
              {/* ── Anticipación ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p style={seccionSt}><i className="fa-solid fa-calendar-days" /> Cuánto se puede reservar hacia adelante</p>
                <Numero
                  label="Máximo de anticipación"
                  value={p.dias_max_anticipacion}
                  onChange={v => set("dias_max_anticipacion", v)}
                  ayuda="Un residente no puede reservar más allá de esta cantidad de días. Poné 0 para no poner tope. La administración no tiene este límite."
                />
                {p.fecha_maxima && Number(p.dias_max_anticipacion) > 0 && (
                  <p style={{ ...ayudaSt, marginTop: -4 }}>
                    <i className="fa-solid fa-arrow-right" style={{ marginRight: 5, color: "#f97316" }} />
                    Hoy, lo más lejos que podrían reservar es el <strong style={{ color: "var(--text-primary)" }}>{p.fecha_maxima}</strong>.
                  </p>
                )}
              </div>

              {/* ── Vencimiento ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid var(--border-card)", paddingTop: 18 }}>
                <p style={seccionSt}><i className="fa-solid fa-hourglass-half" /> Solicitudes sin revisar</p>
                <Numero
                  label="Rechazar sola la solicitud después de"
                  value={p.dias_vence_pendiente}
                  onChange={v => set("dias_vence_pendiente", v)}
                  max={365}
                  ayuda="Si la solicitud sigue pendiente pasados estos días, se rechaza sola y el horario vuelve a quedar libre. El plazo nunca pasa del día anterior a la reserva: quien pida una fecha más cercana que estos días tiene hasta la víspera. Poné 0 para que nunca se rechacen solas."
                />
                {Number(p.dias_vence_pendiente) > 0 && (
                  <Numero
                    label="Avisar al residente antes de rechazarla"
                    value={p.dias_aviso_previo}
                    onChange={v => set("dias_aviso_previo", v)}
                    max={30}
                    ayuda="Días antes del vencimiento en que le llega el aviso de que tiene el pago pendiente. Se manda una sola vez, por correo y por la campanita."
                  />
                )}
              </div>

              {/* ── Cancelación de las que ya pasaron ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid var(--border-card)", paddingTop: 18 }}>
                <p style={seccionSt}><i className="fa-solid fa-calendar-xmark" /> Reservas que ya pasaron</p>
                <Numero
                  label="Cancelar sola la reserva después de"
                  value={p.dias_cancela_tras_fecha}
                  onChange={v => set("dias_cancela_tras_fecha", v)}
                  max={365}
                  ayuda="Días que se le dan a una solicitud que siguió pendiente después de la fecha reservada. El plazo arranca el día siguiente a la reserva: con 3 días, la reserva del 20 de octubre se cancela el 24. La reserva no se borra, queda marcada como cancelación automática. Poné 0 para que nunca se cancelen solas."
                />
                {Number(p.dias_cancela_tras_fecha) > 0 && (
                  <p style={{ ...ayudaSt, marginTop: -4 }}>
                    <i className="fa-solid fa-arrow-right" style={{ marginRight: 5, color: "#f97316" }} />
                    Hoy se cancelarían las que sigan pendientes y sean del{" "}
                    <strong style={{ color: "var(--text-primary)" }}>{fechaCorteCancelacion(p.dias_cancela_tras_fecha)}</strong> o antes.
                  </p>
                )}
              </div>

              {/* ── Morosidad ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 14, borderTop: "1px solid var(--border-card)", paddingTop: 18 }}>
                <p style={seccionSt}><i className="fa-solid fa-ban" /> Quién puede reservar</p>
                <Switch
                  checked={!!p.bloquear_morosos}
                  onChange={v => set("bloquear_morosos", v)}
                  titulo="No dejar reservar a quien está en mora"
                  ayuda="Apagado, cualquier residente puede reservar aunque deba. Nunca bloquea a la administración: ella puede registrar la reserva igual."
                />
                {p.bloquear_morosos && (
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                    gap: 16, padding: "14px 16px", borderRadius: 10,
                    border: "1px solid var(--border-card)", background: "var(--hover-bg)",
                  }}>
                    <Numero
                      label="Se bloquea a partir de"
                      value={p.max_facturas_vencidas}
                      onChange={v => set("max_facturas_vencidas", v)}
                      max={60} sufijo="facturas vencidas"
                      ayuda="0 = no se mira la cantidad de facturas, solo el monto."
                    />
                    <Numero
                      label="Días de gracia después del vencimiento"
                      value={p.dias_gracia_vencimiento}
                      onChange={v => set("dias_gracia_vencimiento", v)}
                      max={120}
                      ayuda="Una factura recién vencida no cuenta hasta pasados estos días."
                    />
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={labelSt}>Deuda máxima tolerada (colones)</label>
                      <input
                        type="number" min="0" step="0.01" value={p.monto_maximo_deuda ?? "0"}
                        onChange={e => set("monto_maximo_deuda", e.target.value)}
                        style={fldStyle({ maxWidth: 200 })}
                      />
                      <p style={ayudaSt}>0 = no se mira el monto. Si se pone, se bloquea a quien deba vencido más que eso.</p>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Switch
                        checked={!!p.respetar_estado_cobro}
                        onChange={v => set("respetar_estado_cobro", v)}
                        titulo="Respetar el estado de cobro de la filial"
                        ayuda='Las marcadas "Al día" o "Arreglo de pago" pueden reservar aunque deban; las de "Cobro judicial" nunca. El estado se marca en Cuentas por Cobrar → Por Rubro.'
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-card)", display: "flex", flexDirection: "column", gap: 12 }}>
          {error && p && (
            <div style={{
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171", borderRadius: 8,
              padding: "10px 14px", color: "#f87171", fontSize: 13,
            }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{
              padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-card)",
              background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
            }}>
              Cancelar
            </button>
            <button onClick={guardar} disabled={saving || cargando || !p} style={{
              padding: "9px 22px", borderRadius: 8, border: "none",
              background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
              cursor: saving || cargando || !p ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
                : <><i className="fa-solid fa-floppy-disk" /> Guardar</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
